<template>
  <div class="">
    <div v-if="!hideHeader" class="mb-2 flex items-center justify-between gap-3">
      <div class="truncate text-sm font-semibold text-white/95">{{ event.fixture || event.name || event.id }}</div>
      <div class="text-xs text-white/55">
        {{ formatTime(event.lineupTime) }} - {{ formatTime(event.endTime) }}
      </div>
    </div>

    <div class="flex flex-col gap-1 transition" :class="isCanceled ? 'opacity-40 grayscale' : ''">
      <!-- Top (status) bars: phase colour, or red/amber where incidents occur. -->
      <div
        class="grid h-7 gap-px overflow-hidden rounded-sm border border-white/10 bg-black/45"
        :style="gridColsStyle"
      >
        <template v-for="cell in trackCells">
          <button
            v-if="cell.incident"
            :key="`tip-${cell.id}`"
            class="m-0 h-full w-full border-0 p-0 transition duration-100"
            :class="[
              cell.baseClass,
              isSelectedIncidentCell(cell) ? 'ring-2 ring-inset ring-white' : '',
              isHoveredIncidentCell(cell) ? 'brightness-200 saturate-150 ring-2 ring-inset ring-white/80 z-10' : '',
            ]"
            @mouseenter="onCellEnter($event, cell)"
            @mouseleave="hoveredIncidentId = null"
            @click.stop="selectIncident(cell.incident)"
          ></button>
          <div
            v-else
            :key="`cell-${cell.id}`"
            class="m-0 h-full w-full border-0 p-0"
            :class="cell.baseClass"
            @mouseenter="onCellEnter($event, cell)"
          ></div>
        </template>
      </div>

      <!-- Bottom strip: original phase colour (blue service / green event / grey incomplete). -->
      <div
        class="grid h-1.5 gap-px overflow-hidden rounded-sm"
        :style="gridColsStyle"
      >
        <div
          v-for="cell in trackCells"
          :key="`bottom-${cell.id}`"
          class="h-full w-full"
          :class="phaseClass(cell)"
        ></div>
      </div>

      <div v-if="!hideAxis" class="relative mt-0.5 h-5">
        <div
          v-for="mark in timeMarks"
          :key="mark.key"
          class="absolute top-0 flex flex-col whitespace-nowrap"
          :class="axisMarkClass(mark.align)"
          :style="{ left: `${mark.left}%` }"
        >
          <span class="h-1.5 w-px bg-white/35"></span>
          <span class="mt-0.5 text-[10px] text-white/55">{{ mark.label }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import moment from 'moment';
import {
  createTimelineScale,
  severityBg,
  severityRank,
  alignForLeft,
  axisMarkClass,
  dedupeMarks,
  validMs,
  firstMs,
} from '../utils/timelineScale';

const props = defineProps({
  event: {
    type: Object,
    required: true,
  },
  incidents: {
    type: Array,
    default: () => [],
  },
  selectedIncidentId: {
    type: String,
    default: null,
  },
  hideHeader: {
    type: Boolean,
    default: false,
  },
  // Suppress the built-in time axis (used when an outer component already
  // renders a shared axis, e.g. the resource-timeline overview row).
  hideAxis: {
    type: Boolean,
    default: false,
  },
  hasReport: {
    type: Boolean,
    default: false,
  },
  // Number of status-track chunks. Measured once by the parent (every row
  // shares the same width) and passed down, so each row doesn't run its own
  // ResizeObserver. See EventAnalytics.vue measureBins().
  binCount: {
    type: Number,
    default: 40,
  },
});

const emit = defineEmits(['select-incident']);

const hoveredIncidentId = ref(null);

function formatTime(value) {
  if (!value) return '--:--';
  return moment.utc(value).format('DD/MM HH:mm');
}
function formatTimeDetailed(value) {
  if (!value) return '--';
  return moment.utc(value).format('DD/MM HH:mm:ss');
}
function overlaps(aStart, aEnd, bStart, bEnd) {
  return aStart < bEnd && bStart < aEnd;
}

const gridColsStyle = computed(() => ({ gridTemplateColumns: `repeat(${props.binCount}, minmax(0, 1fr))` }));
// Whole timeline window: service window padding + the event itself.
const lineupMs = computed(() => firstMs([props.event.serviceStartTime, props.event.lineupTime, props.event.startTime], 0, Math.min));
const endMs = computed(() => firstMs([props.event.serviceEndTime, props.event.endTime, props.event.startTime], lineupMs.value + 1, Math.max));
// The actual event window (green); everything else in range is "service" (blue).
const eventStartMs = computed(() => {
  const value = validMs(props.event.startTime);
  if (value !== null) return value;
  return validMs(props.event.serviceStartTime) !== null ? validMs(props.event.serviceStartTime) : lineupMs.value;
});
const eventEndMs = computed(() => {
  const value = validMs(props.event.endTime);
  if (value !== null) return value;
  return validMs(props.event.serviceEndTime) !== null ? validMs(props.event.serviceEndTime) : endMs.value;
});
// Kept for axis marks compatibility.
const startMs = computed(() => eventStartMs.value);
const safeDurationMs = computed(() => {
  const raw = endMs.value - lineupMs.value;
  return raw > 0 ? raw : 1;
});
// Time -> percent scale for the whole event window (axis marks only; the
// status track itself uses CSS grid bins, not absolute positioning).
const scale = computed(() => createTimelineScale({ start: lineupMs.value, end: endMs.value }));
const normalizedIncidents = computed(() => (props.incidents || [])
  .map((item) => ({
    ...item,
    startMs: Number(item.startMs),
    endMs: Number(item.endMs),
  }))
  .filter((item) => Number.isFinite(item.startMs) && Number.isFinite(item.endMs) && item.endMs >= item.startMs)
  .sort((a, b) => a.startMs - b.startMs));
const isStatusCompleted = computed(() => {
  const status = `${(props.event && props.event.status) || ''}`.toUpperCase();
  return /COMPLETE|COMPLETED|ENDED|FINISH|DONE|ARCHIV/.test(status);
});
// Cancelled events (status "Canceled" / "late_canceled") are dimmed out — the
// timeline is shown greyed since the event never ran as scheduled.
const isCanceled = computed(() => {
  const status = `${(props.event && props.event.status) || ''}`.toUpperCase();
  return /CANCEL/.test(status);
});

// Flatten every incident's anomalies into time spans, keeping a reference to
// the parent incident. The status track colours each bin by the anomalies
// actually active during that bin (not the incident's overall severity), so a
// mostly-warning incident with a brief error shows amber with a red slice
// rather than a solid red block. Incidents without per-anomaly detail fall
// back to a single span covering the whole incident window.
const anomalySpans = computed(() => {
  const spans = [];
  normalizedIncidents.value.forEach((incident) => {
    const anomalies = Array.isArray(incident.anomalies) ? incident.anomalies : [];
    if (!anomalies.length) {
      spans.push({ startMs: incident.startMs, endMs: incident.endMs, severity: incident.severity, incident });
      return;
    }
    anomalies.forEach((anomaly) => {
      const start = Number(anomaly.startMs);
      const end = Number(anomaly.endMs);
      if (!Number.isFinite(start) || !Number.isFinite(end)) return;
      spans.push({ startMs: start, endMs: Math.max(end, start), severity: anomaly.severity, incident });
    });
  });
  return spans;
});

// Highest-severity anomaly active within [cellStart, cellEnd], plus the
// incident it belongs to (the click/select target). Returns null when no
// anomaly is active in this bin, so quiet gaps keep the underlying phase colour.
function findBinIncident(cellStart, cellEnd) {
  let best = null;
  let bestRank = -1;
  anomalySpans.value.forEach((span) => {
    // Info-level anomalies are not surfaced on the overview track.
    if (span.severity === 'info') return;
    if (!overlaps(cellStart, cellEnd, span.startMs, span.endMs)) return;
    const rank = severityRank(span.severity);
    if (rank > bestRank) {
      bestRank = rank;
      best = span;
    }
  });
  return best ? { incident: best.incident, severity: best.severity } : null;
}
// Bottom strip: always the underlying phase colour, regardless of report
// state. blue = service window, green = the event.
function phaseClass(cell) {
  return cell.phase === 'event' ? 'bg-emerald-500' : 'bg-cyan-700';
}
// Top bar base colour: grey where there's no report data, red/amber where
// incidents occur, otherwise the phase colour. Selection ring is applied
// separately in the template so this stays independent of selectedIncidentId.
function cellBaseClass(cell) {
  if (cell.incomplete) return 'bg-neutral-500';
  if (cell.incident) return severityBg(cell.severity);
  return phaseClass(cell);
}

const trackCells = computed(() => {
  const cells = [];
  const binMs = safeDurationMs.value / props.binCount;
  const now = Date.now();
  for (let i = 0; i < props.binCount; i += 1) {
    const rangeStart = lineupMs.value + (i * binMs);
    const rangeEnd = rangeStart + binMs;
    const binIncident = findBinIncident(rangeStart, rangeEnd);
    const incident = binIncident ? binIncident.incident : null;
    const severity = binIncident ? binIncident.severity : null;
    const center = (rangeStart + rangeEnd) / 2;
    // Green = within the actual event window; blue = service padding around it.
    const phase = (center >= eventStartMs.value && center < eventEndMs.value) ? 'event' : 'service';
    // A slice is "incomplete" (grey) when it has no report data: either the
    // period hasn't elapsed yet (live event tail), or the event is already
    // completed but its report was never generated, so nothing was processed.
    const future = rangeStart > now;
    const incomplete = !props.hasReport && (isStatusCompleted.value || future);
    const status = incomplete
      ? (future ? 'future' : 'no report')
      : (severity || phase);
    const cell = {
      id: i,
      rangeStart,
      rangeEnd,
      phase,
      incomplete,
      status,
      incident,
      severity,
    };
    // Precompute the base colour class only. The (moment-heavy) tooltip
    // title is built lazily on hover (see onCellEnter) so we don't run
    // moment for every one of the ~240 cells on each render.
    cell.baseClass = cellBaseClass(cell);
    cells.push(cell);
  }
  return cells;
});
const timeMarks = computed(() => {
  const marks = [
    {
      key: 'lineup',
      time: lineupMs.value,
      label: `${formatTime(props.event.lineupTime || lineupMs.value)}`,
    },
    {
      key: 'start',
      time: startMs.value,
      label: `${formatTime(props.event.startTime || startMs.value)}`,
    },
    {
      key: 'end',
      time: endMs.value,
      label: `${formatTime(props.event.endTime || endMs.value)}`,
    },
  ];

  return dedupeMarks(
    marks.map((mark) => {
      const left = scale.value.percent(mark.time);
      return { ...mark, left, align: alignForLeft(left) };
    }),
  );
});

function incidentTooltip(incident) {
  const start = formatTimeDetailed(incident.startMs);
  const end = formatTimeDetailed(incident.endMs);
  const count = Array.isArray(incident.anomalies) ? incident.anomalies.length : null;
  const countLabel = count != null ? ` | ${count} ${count === 1 ? 'anomaly' : 'anomalies'}` : '';
  const types = Array.isArray(incident.resourceTypes) && incident.resourceTypes.length
    ? `\n${incident.resourceTypes.join(', ')}`
    : '';
  return `${incident.severity.toUpperCase()}${countLabel} | ${start} -> ${end}${types}`;
}
function computeCellTitle(cell) {
  if (cell.incident) return incidentTooltip(cell.incident);
  return `${cell.status.toUpperCase()} | ${formatTimeDetailed(cell.rangeStart)} -> ${formatTimeDetailed(cell.rangeEnd)}`;
}
// Build and assign the cell's tooltip lazily on hover. moment formatting is
// the expensive part, so deferring it until a cell is actually hovered keeps
// the per-row render cheap. Recomputed each enter so it always matches the
// current cell (DOM elements are reused across re-renders).
function onCellEnter(event, cell) {
  if (cell.incident) hoveredIncidentId.value = cell.incident.id;
  if (event.currentTarget) event.currentTarget.title = computeCellTitle(cell);
}
function isSelectedIncidentCell(cell) {
  return !!(cell.incident && props.selectedIncidentId && cell.incident.id === props.selectedIncidentId);
}
// True for every cell belonging to the incident currently hovered, so the
// whole incident block lights up together.
function isHoveredIncidentCell(cell) {
  return !!(cell.incident && hoveredIncidentId.value && cell.incident.id === hoveredIncidentId.value);
}
function selectIncident(incident) {
  emit('select-incident', incident);
}
</script>
